// src/components/Organizer/EventList.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "../../utils/api";
import { toast } from "react-toastify";
import { formatJapaneseDateWithWeekday } from "../../utils/dateUtils";

// Event type definition to match API response
type Event = {
	id: number;
	external_id: string;
	title: string;
	slug: string;
	date: string;
	time: string;
	venue_name: string;
	prefecture: string;
	city: string;
	address: string;
	hashtags?: string[];
	status: "draft" | "published";
	image_url: string;
	totalTickets: number;
	soldTickets: number;
	description?: string;
	opening_times?: {
		id: number;
		open_time: string;
		start_time: string;
		end_time: string;
		description?: string;
	}[];
};

const EventList: React.FC = () => {
	const navigate = useNavigate();
	const [filter, setFilter] = useState("all");
	const [events, setEvents] = useState<Event[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// API からイベントデータを取得
	useEffect(() => {
		const fetchEvents = async () => {
			try {
				// クエリパラメータでフィルタリングも可能
				// 例: /organizer/events?status=published&timeframe=upcoming
				const queryParams = new URLSearchParams();
				if (filter === "published" || filter === "draft") {
					queryParams.append("status", filter);
				}
				if (filter === "upcoming" || filter === "past") {
					queryParams.append("timeframe", filter);
				}

				const response = await apiClient.get(
					`/organizer/events?${queryParams.toString()}`
				);
				setEvents(response.data.events);
				setIsLoading(false);
			} catch (error: any) {
				console.error("イベントデータの取得に失敗しました:", error);
				setError("イベントデータの取得に失敗しました。");
				setIsLoading(false);

				// エラー表示
				toast.error("イベントデータの取得に失敗しました");
			}
		};

		setIsLoading(true);
		fetchEvents();
	}, [filter]);

	// APIのフィルタリングを使用する場合は、フロントエンドでの追加フィルタリングは不要
	// ただし必要に応じて、レスポンスデータを追加でフィルタリングすることも可能

	const handleDeleteEvent = async (eventExternalId: string) => {
		if (!window.confirm("このイベントを削除してもよろしいですか？")) {
			return;
		}

		try {
			await apiClient.delete(`/organizer/events/${eventExternalId}`);
			toast.success("イベントが削除されました");
			// 成功したら、イベントリストから削除したイベントを除外
			setEvents(
				events.filter((event) => event.external_id !== eventExternalId)
			);
		} catch (error: any) {
			console.error("イベント削除に失敗しました:", error);

			if (error.response && error.response.status === 400) {
				// 販売済みチケットがある場合など
				toast.error(error.response.data.message || "イベントを削除できません");
			} else {
				toast.error("イベント削除に失敗しました");
			}
		}
	};

	const renderEventListContent = () => {
		if (error) {
			return (
				<div className="text-center py-10 bg-red-50 dark:bg-red-900/20 rounded-lg">
					<p className="text-red-600 dark:text-red-300">{error}</p>
					<button
						onClick={() => window.location.reload()}
						className="mt-4 inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
					>
						再読み込み
					</button>
				</div>
			);
		}

		if (events.length === 0) {
			return (
				<div className="text-center py-10 bg-gray-50 dark:bg-gray-800 rounded-lg">
					<p className="text-gray-500 dark:text-gray-400">
						イベントがありません
					</p>
					<Link
						to="/organizer/events/create"
						className="text-blue-600 hover:underline mt-2 inline-block"
					>
						イベントを作成する
					</Link>
				</div>
			);
		}

		return (
			<div className="space-y-4">
				{events.map((event) => {
					return (
						<div
							key={event.external_id}
							className="bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
						>
							<div className="flex">
								<div className="w-24 h-24 mr-4 flex-shrink-0">
									<img
										src={event.image_url || "https://via.placeholder.com/150"}
										alt={event.title}
										className="w-full h-full object-cover rounded"
									/>
								</div>
								<div className="flex-1">
									<div className="flex justify-between items-start mb-2">
										<div>
											<h3 className="font-bold text-lg text-gray-900 dark:text-white">
												{event.title}
											</h3>
											<div className="text-gray-600 dark:text-gray-400 space-y-1 mt-2">
												<div>
													<p className="font-medium">
														📅 {formatJapaneseDateWithWeekday(event.date)}
													</p>
													{event.opening_times &&
													event.opening_times.length > 0 ? (
														event.opening_times.length === 1 ? (
															<p className="text-sm ml-5">
																{event.opening_times[0].start_time.replace(
																	/(\d+):(\d+).*/,
																	"$1:$2"
																)}
																開演
															</p>
														) : (
															<div className="text-sm ml-5 space-y-0.5">
																{event.opening_times.map((time, index) => (
																	<p key={index}>
																		{time.description || `第${index + 1}部`}:{" "}
																		{time.start_time.replace(
																			/(\d+):(\d+).*/,
																			"$1:$2"
																		)}
																	</p>
																))}
															</div>
														)
													) : (
														<p className="text-sm ml-5">
															{event.time.replace(/(\d+):(\d+).*/, "$1:$2")}開演
														</p>
													)}
												</div>
												<p>
													📍{" "}
													{event.venue_name ||
														`${event.prefecture} ${event.city}`}
												</p>
											</div>
										</div>
										<div className="flex flex-col items-end">
											<span
												className={`px-3 py-1 rounded-full text-sm font-medium ${
													event.status === "published"
														? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300"
														: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
												}`}
											>
												{event.status === "published" ? "公開中" : "下書き"}
											</span>
											<div className="mt-2 text-gray-600 dark:text-gray-400 text-sm">
												販売状況: {event.soldTickets}/{event.totalTickets} 枚 (
												{event.totalTickets > 0
													? Math.round(
															(event.soldTickets / event.totalTickets) * 100
													  )
													: 0}
												%)
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="flex items-center justify-end mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-x-3">
								<button
									onClick={() => navigate(`/events/${event.slug}`)}
									className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
								>
									プレビュー
								</button>
								<button
									onClick={() =>
										navigate(`/organizer/events/${event.external_id}/tickets`)
									}
									className="px-3 py-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
								>
									チケット管理
								</button>
								<button
									onClick={() =>
										navigate(`/organizer/events/${event.external_id}/edit`)
									}
									className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
								>
									編集
								</button>
								<button
									onClick={() => handleDeleteEvent(event.external_id)}
									className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
								>
									削除
								</button>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="space-y-6">
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
							イベント管理
						</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							登録済みイベントの一覧と管理
						</p>
					</div>
					<Link
						to="/organizer/events/create"
						className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md hover:from-violet-600 hover:to-purple-700 transform transition-all duration-200 hover:-translate-y-0.5"
					>
						新規イベント作成
					</Link>
				</div>

				<div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700 pb-2">
					{[
						{ value: "all", label: "すべて" },
						{ value: "published", label: "公開中" },
						{ value: "draft", label: "下書き" },
						{ value: "past", label: "過去のイベント" },
					].map((option) => (
						<button
							key={option.value}
							className={`px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 ${
								filter === option.value
									? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-sm"
									: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
							}`}
							onClick={() => setFilter(option.value)}
						>
							{option.label}
						</button>
					))}
				</div>
				<div className="relative">
					{renderEventListContent()}
					{isLoading && (
						<div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80 dark:bg-gray-900/70 backdrop-blur-sm">
							<p className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
								<span
									className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin"
									aria-hidden="true"
								/>
								読み込み中...
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default EventList;
